import { useState, useEffect, useRef, useCallback } from "react";

// Réglages batterie : précis + frais en course, économe à l'arrêt
const PROFILES = {
  active: { enableHighAccuracy: true, maximumAge: 3000, timeout: 15000 },
  idle: { enableHighAccuracy: false, maximumAge: 30000, timeout: 20000 },
};

/**
 * Géolocalisation navigateur (iOS Safari / Android Chrome / PWA).
 * - watch=false : une seule position (profils client, commerçant, artisan)
 * - watch=true  : suivi continu, suspendu quand l'onglet est masqué (économie batterie)
 */
export function useGeolocation({ watch = false, mode = "idle", enabled = true } = {}) {
  const [position, setPosition] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | locating | granted | denied | unsupported | error
  const watchId = useRef(null);

  const onSuccess = useCallback((p) => {
    setPosition({
      lat: p.coords.latitude,
      lng: p.coords.longitude,
      accuracy: p.coords.accuracy,
      speed: p.coords.speed,
      heading: p.coords.heading,
      ts: p.timestamp,
    });
    setStatus("granted");
  }, []);

  const onError = useCallback((err) => {
    setStatus(err.code === 1 ? "denied" : "error");
  }, []);

  const request = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setStatus("unsupported");
      return;
    }
    setStatus((s) => (s === "granted" ? s : "locating"));
    navigator.geolocation.getCurrentPosition(onSuccess, onError, PROFILES[mode]);
  }, [mode, onSuccess, onError]);

  // Suivi de l'état de permission quand le navigateur le permet
  useEffect(() => {
    if (!navigator.permissions?.query) return;
    let perm;
    navigator.permissions.query({ name: "geolocation" }).then((p) => {
      perm = p;
      if (p.state === "denied") setStatus("denied");
      p.onchange = () => (p.state === "denied" ? setStatus("denied") : request());
    }).catch(() => {});
    return () => { if (perm) perm.onchange = null; };
  }, [request]);

  useEffect(() => {
    if (!enabled) return;
    if (!watch) {
      request();
      return;
    }
    if (!("geolocation" in navigator)) {
      setStatus("unsupported");
      return;
    }
    const start = () => {
      if (watchId.current != null) return;
      setStatus((s) => (s === "granted" ? s : "locating"));
      watchId.current = navigator.geolocation.watchPosition(onSuccess, onError, PROFILES[mode]);
    };
    const stop = () => {
      if (watchId.current != null) navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    };
    const onVisibility = () => (document.hidden ? stop() : start());
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
    };
  }, [watch, mode, enabled, request, onSuccess, onError]);

  return { position, status, request };
}
